import { Choice } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import { getVariantSavingValue } from '@thg-commerce/enterprise-pricing/src/getVariantSavingValue'

import {
  SubscriptionChoice,
  SelectedOptions,
  SubscriptionData,
} from '../../ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  ProductImage,
  RichContentItem,
  SubscriptionContract,
  SubscriptionFrequency,
} from '../../generated/graphql'
import { productGallery } from './gallery'
import { ProductVariantItem } from '../../ApolloProvider/resolvers/Types/ProductVariant'
import {
  MarketedSpecialOfferSummary,
  productMarketedSpecialOffer,
} from './marketedSpecialOffer'

export enum SubscriptionOptionsPaymentType {
  UPFRONT = 'Upfront',
  ON_DISPATCH = 'On_Dispatch',
  ON_ORDER = 'On_Order',
}

type CombinedChoice = Choice & SubscriptionChoice

export type Variant = {
  title: string
  images: ProductImage[]
  price?: {
    price: {
      displayValue: string
      scalarValue: string
      amount: string
    }
    rrp: {
      displayValue: string
      scalarValue: string
      amount: string
    }
  } | null
  returnsInformation: RichContentItem | null
  deliveryInformation: RichContentItem | null
  deliveryReturnsContentDefined: boolean
  isSubscription: boolean
  isAutoRenewSubscription: boolean
  subscriptionFrequency?: SubscriptionFrequency
  subscriptionTerm?: number
  subscriptionPaymentType?: SubscriptionOptionsPaymentType
  subscriptionContracts: SubscriptionContract[]
  marketedSpecialOffer: MarketedSpecialOfferSummary | null
  selectedOptions?: SelectedOptions | null
  choices: CombinedChoice[]
}

type Product = {
  sku: number
  linkedOn?: string | null
}

type InputVariant = Variant & {
  product?: Product | null
}

type ProductWithVariants = Product & {
  defaultVariant?: Variant | null
  variants: InputVariant[]
}

export const defaultVariant = (
  product: ProductWithVariants,
  enablePreselectedProductVariant?: boolean,
): (Variant | InputVariant) & {
  gallery: ReturnType<typeof productGallery>
  subscriptionData: SubscriptionData
} => {
  let selectedOptions = {}
  const subscriptionOptions = {
    upfront: (product?.variants || []).reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.UPFRONT
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
    onDispatch: (product?.variants || []).reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.ON_DISPATCH
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
  }
  if (product.defaultVariant) {
    if (product.defaultVariant.choices) {
      selectedOptions = enablePreselectedProductVariant
        ? product.defaultVariant.choices.reduce<SelectedOptions>(
            (accumulator, choice) => {
              accumulator[choice.optionKey] = choice.key

              return accumulator
            },
            {},
          )
        : product.defaultVariant.choices.reduce<SelectedOptions>(
            (accumulator, choice) => {
              if (choice.optionKey === product.linkedOn) {
                accumulator[choice.optionKey] = choice.key
              }
              return accumulator
            },
            {},
          )
    }

    return {
      ...product.defaultVariant,
      selectedOptions,
      gallery: productGallery(product.defaultVariant),
      returnsInformation: product.defaultVariant.returnsInformation
        ?.content?.[0]?.content
        ? product.defaultVariant.returnsInformation.content[0].content
        : null,
      deliveryInformation: product.defaultVariant.deliveryInformation
        ?.content?.[0]?.content
        ? product.defaultVariant.deliveryInformation.content[0].content
        : null,
      deliveryReturnsContentDefined:
        product.defaultVariant.deliveryInformation?.content?.[0]?.content &&
        product.defaultVariant.returnsInformation?.content?.[0]?.content
          ? true
          : false,
      subscriptionData: {
        ...getSubscriptionData(product.defaultVariant),
        subscribeOptions: subscriptionOptions,
      },
      marketedSpecialOffer: product.defaultVariant?.marketedSpecialOffer
        ? productMarketedSpecialOffer(product.defaultVariant)
        : null,
    }
  }

  const masterSKU = product.sku
  const masterProductVariant = product.variants.find(
    (variant) => variant.product?.sku === masterSKU,
  )

  const defaultVariant = masterProductVariant || product.variants[0]

  if (defaultVariant && defaultVariant.choices) {
    selectedOptions = enablePreselectedProductVariant
      ? defaultVariant.choices.reduce<SelectedOptions>(
          (accumulator, choice) => {
            accumulator[choice.optionKey] = choice.key

            return accumulator
          },
          {},
        )
      : defaultVariant.choices.reduce<SelectedOptions>(
          (accumulator, choice) => {
            if (choice.optionKey === product.linkedOn) {
              accumulator[choice.optionKey] = choice.key
            }
            return accumulator
          },
          {},
        )
  }

  return {
    ...defaultVariant,
    selectedOptions,
    gallery: productGallery(defaultVariant),
    returnsInformation: defaultVariant.returnsInformation?.content?.[0]?.content
      ? defaultVariant.returnsInformation.content[0].content
      : null,
    deliveryInformation: defaultVariant.deliveryInformation?.content?.[0]
      ?.content
      ? defaultVariant.deliveryInformation.content[0].content
      : null,
    deliveryReturnsContentDefined:
      defaultVariant.deliveryInformation?.content?.[0]?.content &&
      defaultVariant.returnsInformation?.content?.[0]?.content
        ? true
        : false,

    subscriptionData: getSubscriptionData(defaultVariant),
    marketedSpecialOffer: defaultVariant?.marketedSpecialOffer
      ? productMarketedSpecialOffer(defaultVariant)
      : null,
  }
}

const getSubscriptionData = (
  variant: Variant | InputVariant,
): SubscriptionData => {
  return {
    isSubscription: variant.isSubscription ?? false,
    isAutoRenewSubscription: variant.isAutoRenewSubscription ?? false,
    subscriptionFrequency: variant.subscriptionFrequency ?? null,
    subscriptionTerm: variant.subscriptionTerm ?? null,
    subscriptionPaymentType: variant.subscriptionPaymentType ?? null,
    subscriptionContracts: variant?.subscriptionContracts ?? [],
    choices:
      variant.isSubscription && variant.choices.length
        ? [
            {
              ...variant.choices[0],
              price: variant.price?.price.displayValue,
              saving: variant.price
                ? getVariantSavingValue(variant.price)
                : null,
            },
          ]
        : [],
  }
}

export type ProductWithDefaultVariant = {
  sku: number
  variants: ProductVariantItem[]
  defaultVariant?: ProductVariantItem
}

export const getDefaultVariantNew = (product?: ProductWithDefaultVariant) => {
  if (!product) {
    return null
  }

  if (product.defaultVariant) {
    return product.defaultVariant
  }

  const masterSKU = product.sku
  const masterProductVariant = product.variants.find(
    (variant) => variant.product?.sku === masterSKU,
  )
  const defaultVariant = masterProductVariant || product.variants[0]
  return defaultVariant
}
